import { useState, Fragment, useEffect, useMemo } from 'react';
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import Pagination from "@mui/material/Pagination";
import SearchResultBlock from "../../components/Block";
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { getPerilAIResponses } from '../address/data4homeAPI';

type PerilAIResponseGroup = {
    url: string,
    content: {
        extractedContent: string,
    },
    responses: PerilAIResponse[],
};

type PerilAIResponse = {
    id: string,
    values?: {
        address: string,
        date: string,
    };
    originalResponse?: any;
    error?: string;
    metadata?: {
        aiProvider: string;
        parameters: any;
    };
};

function ResponseItem({ response }: { response: PerilAIResponse }) {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation();

    return (
        <Paper elevation={1} sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={1}>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2">
                            {response.values?.address || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2">
                            {response.values?.date || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2">
                            {response.metadata?.aiProvider || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2">
                            {response.metadata?.parameters?.model || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color={response.error ? 'error' : 'inherit'}>
                            {response.error || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
            {expanded && (
                <>
                    <Divider />
                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('details')}
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, maxHeight: '300px', overflow: 'auto', backgroundColor: 'white' }}>
                            <Typography variant="body2" component="pre" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                                {response.metadata?.parameters?.messages?.[1]?.content || '-'}
                            </Typography>
                        </Paper>
                    </Box>
                </>
            )}
        </Paper>
    );
}

function ResponseGroup({ group }: { group: PerilAIResponseGroup }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(group.url).then(() => {
            enqueueSnackbar(t('urlCopied'), { variant: 'success' });
        }).catch(() => {
            enqueueSnackbar(t('copyFailed'), { variant: 'error' });
        });
    };

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton
                    size="small"
                    onClick={() => window.open(group.url, '_blank', 'noopener,noreferrer')}
                    sx={{ mr: 1 }}
                >
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {group.url}
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleCopyUrl}
                    sx={{ mr: 1 }}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </Box>
            {expanded && (
                <Paper elevation={1} sx={{ mb: 2, overflow: 'hidden' }}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            {t('extractedContent')}
                        </Typography>
                        <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                            <Typography variant="body2" component="pre" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                                {group.content?.extractedContent || '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}
            <Typography variant="subtitle1" gutterBottom>
                {t('aiModelResponses')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
                <Grid item xs={1}></Grid>
                <Grid item xs={3}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('address')}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('date')}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('aiProvider')}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('model')}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('error')}
                    </Typography>
                </Grid>
            </Grid>
            {group.responses.map((response) => (
                <ResponseItem key={response.id} response={response} />
            ))}
        </Box>
    );
}

export default function PerilAI() {
    const [responses, setResponses] = useState<PerilAIResponseGroup[]>([]);
    const [responseCount, setResponseCount] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const itemsPerPage = 25;

    const fetchResponses = useMemo(() => {
        return (pageNumber: number) => {
            getPerilAIResponses({ page: pageNumber, limit: itemsPerPage })
                .then(data => {
                    setResponses(data.data);
                    setResponseCount(data.pagination.total);
                })
                .catch((error) => {
                    enqueueSnackbar(
                        t('error.fetchingPerils'),
                        { variant: "error" }
                    );
                });
        }
    }, [enqueueSnackbar, t]);

    useEffect(function() {
        fetchResponses(page);
    }, [fetchResponses, page]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <Fragment>
            <SearchResultBlock title={t('perilAI.responses')}>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('perilAI.showingResults', {
                                start: (page - 1) * itemsPerPage + 1,
                                end: Math.min(page * itemsPerPage, responseCount),
                                total: responseCount
                            })}
                        </Typography>
                        <Pagination
                            count={Math.ceil(responseCount / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                        />
                    </Box>
                    <Box>
                        {responses.map((group) => (
                            <ResponseGroup key={group.url} group={group} />
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('perilAI.showingResults', {
                                start: (page - 1) * itemsPerPage + 1,
                                end: Math.min(page * itemsPerPage, responseCount),
                                total: responseCount
                            })}
                        </Typography>
                        <Pagination
                            count={Math.ceil(responseCount / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                        />
                    </Box>
                </Stack>
            </SearchResultBlock>
        </Fragment>
    );
}
